import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import commandPaletteStore from "Stores/CommandPaletteStore";
import copilotStore from "Stores/copilotStore";
import Utils from "Utils/utils";
import userDetail from "Stores/userDetail";
import { MXP_EVENT } from "Static/MixPanelEvents";
import { useDebounce } from "Components/common/hooks/useDebounce";
import SparkleIcon from "Components/common/SparkleIcon";
import EntityAvatar from "./EntityAvatar";
import {
  COMMAND_PALETTE_OPTIONS,
  matchesQuery,
  highlightSegments
} from "./commandPaletteOptions";
import {
  searchContacts,
  searchAccounts,
  ENTITY_RESULT_LIMIT
} from "./commandPaletteSearch";

const PAGE_MATCH_LIMIT = 6;

// Empty-query state: grouped defaults (Quick actions, Pages). Unchanged.
const buildDefaultItems = () => {
  const display = [];
  const selectable = [];
  const defaults = COMMAND_PALETTE_OPTIONS.filter(o => o.defaultShown);
  const grouped = {};
  defaults.forEach(o => {
    const g = o.group || "Pages";
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(o);
  });
  ["Quick actions", "Pages"].forEach(g => {
    if (!grouped[g]) return;
    display.push({ kind: "header", text: g });
    grouped[g].forEach(o => {
      display.push({ kind: "item", item: o });
      selectable.push(o);
    });
  });
  return { display, selectable };
};

/**
 * Query state. Order is Contacts → Accounts → Pages → Ask AI (bottom-pinned).
 * Entity rows (contacts/accounts) come from the network, so they live in their
 * own block that eases in; pages + Ask AI are computed locally and render
 * instantly. `selectable` is the flat keyboard-nav order and MUST match the
 * render order (group rows, then staticRows, then askAIItem).
 */
const buildQueryItems = (query, entities, loading) => {
  const trimmed = query.trim();
  const selectable = [];
  // Each entity group is its own row-set so it can ease in independently —
  // a late-arriving group grows into place instead of snapping the others.
  const groups = [];
  const staticRows = [];

  const { contacts = [], accounts = [] } = entities || {};

  const pushGroup = (entityKey, heading, items, full) => {
    if (!items.length) return;
    const rows = [{ kind: "header", text: heading, loading }];
    items.forEach(item => {
      rows.push({ kind: "item", item });
      selectable.push(item);
    });
    if (full) {
      const viewAll = {
        id: `view-all-${entityKey}`,
        type: "viewAll",
        entity: entityKey,
        label: `View all ${entityKey}`
      };
      rows.push({ kind: "item", item: viewAll });
      selectable.push(viewAll);
    }
    groups.push({ key: entityKey, rows });
  };

  pushGroup("contacts", "Contacts", contacts, contacts.length >= ENTITY_RESULT_LIMIT);
  pushGroup("accounts", "Accounts", accounts, accounts.length >= ENTITY_RESULT_LIMIT);

  // Pages — "Go to" navigation matches.
  const pageMatches = COMMAND_PALETTE_OPTIONS.filter(o =>
    matchesQuery(o, trimmed)
  ).slice(0, PAGE_MATCH_LIMIT);
  if (pageMatches.length > 0) {
    staticRows.push({ kind: "header", text: "Go to" });
    pageMatches.forEach(o => {
      staticRows.push({ kind: "item", item: o });
      selectable.push(o);
    });
  }

  // Ask AI — always available, pinned at the very bottom.
  const askAIItem = {
    id: "ask-ai",
    type: "askAI",
    query: trimmed,
    iconName: "auto_awesome"
  };
  selectable.push(askAIItem);

  return { groups, staticRows, askAIItem, selectable };
};

const Breadcrumb = ({ breadcrumb, query }) => (
  <span className="commandPaletteBreadcrumb">
    {breadcrumb.map((seg, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="separator">›</span>}
        <span className="segment">
          {highlightSegments(seg, query).map((piece, j) =>
            piece.match ? (
              <span key={j} className="highlight">{piece.text}</span>
            ) : (
              <span key={j}>{piece.text}</span>
            )
          )}
        </span>
      </React.Fragment>
    ))}
  </span>
);

const HighlightedText = ({ text, query }) => (
  <>
    {highlightSegments(text, query).map((piece, j) =>
      piece.match ? (
        <span key={j} className="highlight">{piece.text}</span>
      ) : (
        <span key={j}>{piece.text}</span>
      )
    )}
  </>
);

const CommandPalette = observer(
  ({ onComposeEmail, onOpenDialer, onOpenCalendar }) => {
    const history = useHistory();
    const { isOpen, query, selectedIndex } = commandPaletteStore;
    const inputRef = useRef(null);
    const userEmail = userDetail?.userInfo?.email || "";

    // Stale-while-revalidate: hold the last results on screen while the next
    // request is in flight so the list never blanks out and re-expands.
    const [entities, setEntities] = useState({ contacts: [], accounts: [] });
    const [entitiesLoading, setEntitiesLoading] = useState(false);
    // Short debounce so the search fires quickly after typing (the call's
    // trigger timing — not API speed — is what made it feel slow). Stale
    // requests are cancelled + guarded, so extra in-flight calls are safe.
    const debouncedQuery = useDebounce(query.trim(), 150);
    // Guards against a slow earlier response overwriting a newer query's data.
    const latestQueryRef = useRef("");

    useEffect(() => {
      const q = debouncedQuery;
      latestQueryRef.current = q;
      if (!q) {
        setEntities({ contacts: [], accounts: [] });
        setEntitiesLoading(false);
        return;
      }
      setEntitiesLoading(true);

      // Decoupled: both fire in parallel, but each group renders the moment
      // ITS OWN call returns — the fast contacts autocomplete no longer waits
      // on the heavier accounts endpoint. Stale results are dropped via the
      // latestQueryRef guard (and each search cancels its own prior request).
      const contactsP = searchContacts(q).then(contacts => {
        if (latestQueryRef.current === q) {
          setEntities(prev => ({ ...prev, contacts }));
        }
        return contacts;
      });
      const accountsP = searchAccounts(q).then(accounts => {
        if (latestQueryRef.current === q) {
          setEntities(prev => ({ ...prev, accounts }));
        }
        return accounts;
      });

      // Both never reject (each catches internally), so Promise.all is safe.
      // Fire the search event + clear loading once both have settled.
      Promise.all([contactsP, accountsP]).then(([contacts, accounts]) => {
        if (latestQueryRef.current !== q) return;
        setEntitiesLoading(false);
        Utils.mixpanelTrack(MXP_EVENT.COMMAND_PALETTE_SEARCHED, {
          query: q,
          contactsCount: contacts.length,
          accountsCount: accounts.length,
          pageMatches: COMMAND_PALETTE_OPTIONS.filter(o => matchesQuery(o, q))
            .length,
          userEmail
        });
      });
    }, [debouncedQuery]);

    const isQueryMode = query.trim().length > 0;

    const { display: defaultDisplay, selectable: defaultSelectable } = useMemo(
      () => buildDefaultItems(),
      []
    );
    const queryItems = useMemo(
      () => buildQueryItems(query, entities, entitiesLoading),
      [query, entities, entitiesLoading]
    );

    const selectable = isQueryMode
      ? queryItems.selectable
      : defaultSelectable;

    useEffect(() => {
      if (isOpen) {
        if (inputRef.current) inputRef.current.focus();
        Utils.mixpanelTrack(MXP_EVENT.COMMAND_PALETTE_OPENED, { userEmail });
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return undefined;
      const handler = e => {
        if (e.key === "Escape") {
          e.preventDefault();
          commandPaletteStore.close();
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          commandPaletteStore.moveSelection(1, Math.max(selectable.length - 1, 0));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          commandPaletteStore.moveSelection(-1, Math.max(selectable.length - 1, 0));
        } else if (e.key === "Enter") {
          e.preventDefault();
          const item = selectable[selectedIndex];
          activateItem(item, e.shiftKey);
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }, [isOpen, selectable, selectedIndex]);

    const askAI = q => {
      if (!q || !q.trim()) return;
      // Opens a NEW copilot chat with the typed question already sent, the way
      // the old AI Assistant behaved. askWithQuery was used here before, but it
      // only toggles CopilotWidget, which is not mounted -- so nothing happened.
      copilotStore.setPendingAutoSend(q.trim());
      commandPaletteStore.close();
      history.push("/copilot/new");
    };

    const runAction = actionKey => {
      commandPaletteStore.close();
      if (actionKey === "compose-email" && onComposeEmail) onComposeEmail();
      else if (actionKey === "open-dialer" && onOpenDialer) onOpenDialer();
      else if (actionKey === "open-calendar" && onOpenCalendar) onOpenCalendar();
    };

    const navigate = path => {
      history.push(path);
      commandPaletteStore.close();
    };

    const labelForItem = (item, isShiftAsk) => {
      if (isShiftAsk || item.type === "askAI") return "Ask Copilot";
      if (item.type === "contact" || item.type === "account") return item.name || "";
      if (item.type === "viewAll") return item.label || "";
      return Array.isArray(item.breadcrumb) ? item.breadcrumb.join(" › ") : "";
    };

    const activateItem = (item, shiftEnter) => {
      if (!item) return;
      const isShiftAsk = Boolean(shiftEnter && query.trim());
      // Record what was clicked alongside the search that produced it.
      Utils.mixpanelTrack(MXP_EVENT.COMMAND_PALETTE_RESULT_SELECTED, {
        query: query.trim(),
        resultType: isShiftAsk ? "askAI" : item.type || "",
        resultLabel: labelForItem(item, isShiftAsk),
        resultId: item.id || "",
        userEmail
      });
      if (isShiftAsk) {
        askAI(query);
        return;
      }
      if (item.type === "askAI") {
        askAI(item.query || query);
      } else if (item.type === "action") {
        runAction(item.actionKey);
      } else if (item.type === "contact" && item.id) {
        navigate(`/contacts/${item.id}/activity`);
      } else if (item.type === "account" && item.id) {
        navigate(`/accounts/${item.id}/overview`);
      } else if (item.type === "viewAll") {
        navigate(item.entity === "contacts" ? "/contacts" : "/accounts");
      } else if (item.type === "page" && item.path) {
        navigate(item.path);
      }
    };

    if (!isOpen) return null;

    let runningSelectableIndex = -1;

    const renderRow = row => {
      if (row.kind === "header") {
        return (
          <div
            key={`h-${row.text}`}
            className="commandPaletteSectionHeader"
          >
            <span>{row.text}</span>
            {row.loading && <span className="pulseDot headerPulse" />}
          </div>
        );
      }
      runningSelectableIndex += 1;
      const idx = runningSelectableIndex;
      const item = row.item;
      const isSelected = idx === selectedIndex;
      const isAskAI = item.type === "askAI";
      const isContact = item.type === "contact";
      const isAccount = item.type === "account";
      const isViewAll = item.type === "viewAll";

      let icon = item.iconName || "arrow_forward";
      if (isViewAll) icon = "more_horiz";

      return (
        <div
          key={item.id || `r-${idx}`}
          className={`commandPaletteRow ${isSelected ? "selected" : ""} ${
            isAskAI ? "askAIRow" : ""
          } ${isViewAll ? "viewAllRow" : ""}`}
          onMouseEnter={() => commandPaletteStore.setSelectedIndex(idx)}
          onClick={() => activateItem(item, false)}
          role="option"
          aria-selected={isSelected}
        >
          {isAskAI ? (
            <span className="rowIcon askAIIcon">
              <SparkleIcon />
            </span>
          ) : isContact || isAccount ? (
            <EntityAvatar
              type={item.type}
              name={item.name}
              domain={item.domain}
            />
          ) : (
            <i className="material-icons-outlined rowIcon">{icon}</i>
          )}
          {isAskAI ? (
            <span className="commandPaletteBreadcrumb">
              <span className="askAILabel">Ask Copilot</span>
              <span className="askAISeparator">—</span>
              <span className="askAIQuery">{item.query || query.trim()}</span>
            </span>
          ) : isContact || isAccount ? (
            <span className="commandPaletteEntity">
              <span className="entityName">
                <HighlightedText text={item.name} query={query} />
              </span>
              {(isContact ? item.email || item.company : item.domain) && (
                <span className="entitySub">
                  <HighlightedText
                    text={isContact ? item.email || item.company : item.domain}
                    query={query}
                  />
                </span>
              )}
            </span>
          ) : isViewAll ? (
            <span className="viewAllLabel">
              <span>{item.label}</span>
              <i className="material-icons-outlined viewAllArrow">arrow_forward</i>
            </span>
          ) : (
            <Breadcrumb breadcrumb={item.breadcrumb} query={query} />
          )}
          {isSelected && !isViewAll && (
            <i className="material-icons-outlined rowArrow">arrow_forward</i>
          )}
        </div>
      );
    };

    // Query mode always has at least the Ask AI row, so it's never "empty".
    const hasAnyRows = isQueryMode || defaultDisplay.length > 0;

    // Portal to <body> so the overlay escapes the header's stacking context
    // (.sgHeader is position:fixed; z-index:12). Without this, the palette's
    // z-index is trapped under z:12 and app modals (compose = z:10000) cover it.
    return createPortal(
      <div
        className="commandPaletteOverlay"
        onClick={() => commandPaletteStore.close()}
        role="presentation"
      >
        <div
          className="commandPaletteModal"
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Search and ask Copilot"
        >
          <div className="commandPaletteInputRow">
            <i className="material-icons-outlined searchIcon">search</i>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search people, accounts, pages — or ask AI…"
              value={query}
              onChange={e => commandPaletteStore.setQuery(e.target.value)}
              maxLength={500}
              aria-label="Command palette search"
            />
            <span className="escKbd">Esc</span>
          </div>

          <div
            className={`commandPaletteResults ${
              isQueryMode ? "queryMode" : ""
            }`}
          >
            {!hasAnyRows && (
              <div className="commandPaletteEmpty">No results found.</div>
            )}

            {isQueryMode ? (
              <>
                {queryItems.groups.map(g => (
                  <div key={g.key} className="commandPaletteEntityGroup">
                    <div className="commandPaletteEntityGroupInner">
                      {g.rows.map(renderRow)}
                    </div>
                  </div>
                ))}
                {queryItems.staticRows.map(renderRow)}
                {renderRow({ kind: "item", item: queryItems.askAIItem })}
              </>
            ) : (
              defaultDisplay.map(renderRow)
            )}
          </div>

          <div className="commandPaletteFooter">
            <span className="hint">
              <span className="kbd">↑</span>
              <span className="kbd">↓</span>
              <span className="hintLabel">to navigate</span>
            </span>
            <span className="hint">
              <span className="kbd">⏎</span>
              <span className="hintLabel">to select</span>
            </span>
            <span className="hint">
              <span className="kbd">⇧</span>
              <span className="kbd">⏎</span>
              <span className="hintLabel">to ask Copilot</span>
            </span>
          </div>
        </div>
      </div>,
      document.body
    );
  }
);

export default CommandPalette;
